import React, { createContext, useContext, useState, useEffect } from 'react';
import { callAPI, IRoleMain, LoacalVariables, Method, setLocalValues } from '../commonFunctions/CommonFunctions';
import socket from '../commonFunctions/socket';
import { resetDataComponent } from './actions';
import { useDispatch } from 'react-redux';

interface User {
  userId: string;
  name: string;
  userType: string;
  cds?: string;
  block?: string;
  district?: string;
}

export interface AuthContextType {
  loading: boolean;
  user: User | null;
  deactive: boolean;
  isAuthenticated: boolean;
  refresh: boolean;
  setUser: (user: User | null) => void;
  setIsAuthenticated: (auth: boolean) => void;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [deactive, setDeactivated] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  const checkAuth = async () => {
    setLoading(true);
    try {
      const res = await callAPI("auth/verify", Method.GET);

      if (res.success) {
        const userData = res.user;
        setUser(userData);
        setIsAuthenticated(true);

        setLocalValues(LoacalVariables.UserId, userData.userId);
        setLocalValues(LoacalVariables.UserType, userData.userType);
        setLocalValues(LoacalVariables.Name, userData.name);

        //user datas;
        setLocalValues(LoacalVariables.Cds, userData.cds);
        setLocalValues(LoacalVariables.Block, userData.block);
        setLocalValues(LoacalVariables.District, userData.district);
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (err) {
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);


  useEffect(() => {
    const accountDeactivated = (data: any) => {
      if (data.targetUserId === user?.userId) {
        setDeactivated(true);
      }
    };

    const handleLoan = () => {
      dispatch(resetDataComponent("loanGrid"));
      setRefresh(true);
    }

    socket.on("repayment-done", handleLoan);
    socket.on("new-loan", handleLoan);
    socket.on("loan-deleted", handleLoan)
    socket.on("user-deactive", accountDeactivated);
    socket.on("loan-status-updated", handleLoan);
    socket.on("loan-updated", handleLoan);

    return () => {
      socket.off("user-deactive", accountDeactivated);
      socket.off("repayment-done", handleLoan);
      socket.off("new-loan", handleLoan);
      socket.off("loan-deleted", handleLoan)
      socket.off("loan-status-updated", handleLoan);
      socket.off("loan-updated", handleLoan);
    };
  }, [user]);


  return (
    <AuthContext.Provider
      value={{
        loading,
        user,
        deactive,
        isAuthenticated,
        refresh,
        setUser,
        setIsAuthenticated,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
